import { experienceText, samePair, yearsText } from '@escala/domain/roleRules';
import { roleIconInfo } from '@escala/domain/roleIcons';
import type { Role, RoleSex } from '@escala/domain/types';

const SEX_LABEL: Record<RoleSex, string> = { any: 'todos', F: 'só feminino', M: 'só masculino' };

/**
 * O texto do aviso que confirma uma alteração na função (Ministérios): diz o que mudou,
 * com os nomes — "Quantidade padrão de Portaria: 3", "Bruno em treinamento no Som".
 */
export function describeRoleChange(
  role: Role,
  patch: Partial<Omit<Role, 'id'>>,
  nameOf: (memberId: string) => string
): string {
  const name = patch.name?.trim() || role.name;

  if (patch.name !== undefined && patch.name.trim() !== role.name) {
    return `Função renomeada para ${patch.name.trim()}`;
  }

  if (patch.icon !== undefined) return `Ícone de ${name}: ${roleIconInfo(patch.icon).label}`;
  if (patch.qty !== undefined) return `Quantidade padrão de ${name}: ${patch.qty}`;
  if (patch.allowedSex !== undefined) return `${name}: sexo permitido — ${SEX_LABEL[patch.allowedSex]}`;

  if (patch.rules !== undefined) {
    const added = patch.rules.find((r) => !role.rules.includes(r));
    const removed = role.rules.find((r) => !patch.rules!.includes(r));

    if (added) return `Regra "${added}" adicionada a ${name}`;
    if (removed) return `Regra "${removed}" removida de ${name}`;

    return `Regras de ${name} atualizadas`;
  }

  if (patch.leaderIds !== undefined) {
    const added = patch.leaderIds.find((id) => !role.leaderIds.includes(id));
    const removed = role.leaderIds.find((id) => !patch.leaderIds!.includes(id));

    if (added) return `${nameOf(added)} agora é Líder de Função de ${name}`;
    if (removed && !patch.leaderIds.length) return `${name} ficou sem Líder de Função`;
    if (removed) return `${nameOf(removed)} deixou de liderar ${name}`;

    return `Líderes de ${name} atualizados`;
  }

  if (patch.critical !== undefined) {
    return patch.critical ? `${name} marcada como função crítica` : `${name} deixou de ser função crítica`;
  }

  if (patch.minExperience !== undefined) {
    return patch.minExperience
      ? `Experiência mínima de ${name}: ${experienceText(patch.minExperience)}`
      : `${name} sem exigência de experiência`;
  }

  if (patch.minYears !== undefined) {
    return patch.minYears
      ? `Tempo de casa mínimo de ${name}: ${yearsText(patch.minYears)}`
      : `${name} sem exigência de tempo de casa`;
  }

  if (patch.trainees !== undefined) {
    const added = patch.trainees.find((id) => !role.trainees.includes(id));
    const removed = role.trainees.find((id) => !patch.trainees!.includes(id));

    if (added) return `${nameOf(added)} em treinamento em ${name}`;
    if (removed) return `Treinamento de ${nameOf(removed)} em ${name} concluído`;
  }

  if (patch.blockedPairs !== undefined) {
    const added = patch.blockedPairs.find((p) => !role.blockedPairs.some((q) => samePair(p, q)));
    const removed = role.blockedPairs.find((p) => !patch.blockedPairs!.some((q) => samePair(p, q)));

    if (added) return `Par restrito em ${name}: ${nameOf(added[0])} × ${nameOf(added[1])}`;
    if (removed) return `Par ${nameOf(removed[0])} × ${nameOf(removed[1])} removido de ${name}`;
  }

  if (patch.togetherPairs !== undefined) {
    const added = patch.togetherPairs.find((p) => !role.togetherPairs.some((q) => samePair(p, q)));
    const removed = role.togetherPairs.find((p) => !patch.togetherPairs!.some((q) => samePair(p, q)));

    if (added) return `Par que serve junto em ${name}: ${nameOf(added[0])} + ${nameOf(added[1])}`;
    if (removed) return `Par ${nameOf(removed[0])} + ${nameOf(removed[1])} não serve mais junto em ${name}`;
  }

  return `${name} atualizada`;
}
